/**
 * Every string a surface shows for a readiness result, resolved from the
 * config the result was scored with. UI, emails and prompts read this instead
 * of the config, so a sentence can only be worded one way.
 *
 * Aliases: readiness view model, readiness copy, describe readiness
 */

import {
  readinessConfigFor,
  type ReadinessBand,
  type ReadinessComponentId,
  type ReadinessStatus,
} from "./config";
import type { ReadinessResult } from "./engine";

export interface ReadinessComponentView {
  id: ReadinessComponentId;
  name: string;
  line: string;
  percent: number;
  status: ReadinessStatus;
  statusLabel: string;
  now: string;
  /** Left out when the component is already Working. */
  workingLooksLike: string | null;
  isHoldingBack: boolean;
}

export interface ReadinessView {
  /** `null` only on a version 1 result past $100K, which gave no score. */
  score: number | null;
  band: Readonly<{ id: ReadinessBand; name: string }> | null;
  /** The pill: the band name, or the past-$100K label where there is no band. */
  bandLabel: string;
  headline: string;
  /** The line under the headline; without a score the headline stands alone. */
  body: string | null;
  capLine: string | null;
  holdingBack: ReadinessComponentId | null;
  /** "Start with lead generation, today." `null` when nothing is holding her back. */
  startWith: string | null;
  components: readonly ReadinessComponentView[];
  counts: Readonly<Record<ReadinessStatus, number>>;
  progressionTitle: string;
  summary: string;
  countLabel: string;
  goalLabel: string;
  /** She answered "$100K or more": the strategy call leads, with the trial beside it. */
  isPastSixFigures: boolean;
  /** At $60K to $100K the trial offer adds the strategy call. */
  offersStrategyCall: boolean;
}

/** Zero reads as "no" in the summary sentence, per the spec. */
function countWord(count: number): string {
  return count === 0 ? "no" : String(count);
}

function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => {
    const value = values[key];
    if (value === undefined) throw new Error(`No value for ${match}`);
    return value;
  });
}

export function describeReadiness(result: ReadinessResult): ReadinessView {
  const config = readinessConfigFor(result.version);
  const { texts } = config;

  const components = config.components.map(
    (component): ReadinessComponentView => {
      const { percent, status } = result.components[component.id];
      return {
        id: component.id,
        name: component.name,
        line: component.line,
        percent,
        status,
        statusLabel: texts.statusLabel[status],
        now: component.status[status],
        workingLooksLike:
          status === "working" ? null : component.status.working,
        isHoldingBack: component.id === result.holdingBack,
      };
    },
  );

  const counts = {
    working: components.filter((c) => c.status === "working").length,
    started: components.filter((c) => c.status === "started").length,
    missing: components.filter((c) => c.status === "missing").length,
  };
  const holdingBack = config.components.find(
    (component) => component.id === result.holdingBack,
  );
  const band = config.bands.find((b) => b.id === result.band) ?? null;
  const isPastSixFigures =
    result.revenue === config.revenue.pastSixFiguresOption;

  const [headline, body] =
    result.score === null
      ? [texts.pastSixFiguresLine, null]
      : holdingBack
        ? [
            `${texts.holdingBackPrefix} ${holdingBack.phrase}`,
            holdingBack.holdingBack,
          ]
        : [texts.allWorkingHeadline, texts.allWorkingLine];

  return {
    score: result.score,
    band: band && { id: band.id, name: band.name },
    bandLabel: band?.name ?? texts.pastSixFiguresBand,
    headline,
    body,
    capLine: result.capped ? texts.capLine : null,
    holdingBack: result.holdingBack,
    startWith: holdingBack
      ? fill(texts.startWith, { phrase: holdingBack.phrase })
      : null,
    components,
    counts,
    progressionTitle: texts.progressionTitle,
    summary: fill(texts.summary, {
      working: countWord(counts.working),
      started: countWord(counts.started),
      missing: countWord(counts.missing),
    }),
    countLabel: fill(texts.countLabel, { working: String(counts.working) }),
    goalLabel: texts.goalLabel,
    isPastSixFigures,
    offersStrategyCall: config.revenue.strategyCallOptions.includes(
      result.revenue,
    ),
  };
}
